import React from 'react';
import { Box, Paper, Typography, Button, Divider, SvgIcon } from '@mui/material';
import { Link, Phone, Reply, Image as ImageIcon, Video, FileText } from 'lucide-react';

const TemplatePreview = ({ template, showHeaderMedia = false }) => {
  const buttonIcons = {
    QUICK_REPLY: Reply,
    URL: Link,
    PHONE_NUMBER: Phone,
  };

  const renderWithSamples = (text, section) => {
    if (!text || typeof text !== 'string') return text || '';
    const samples = template?.variableSamples || {};
    return text.replace(/\{\{\s*(\d+)\s*\}\}/g, (match, name) => {
      const key = name;
      const scopedSample = samples?.[section]?.[key];
      const globalSample = samples?.[key];
      if (scopedSample !== undefined && scopedSample !== null && scopedSample !== '') {
        return scopedSample;
      }
      if (globalSample !== undefined && globalSample !== null && globalSample !== '') {
        return globalSample;
      }
      return match;
    });
  };

  const renderFormattedText = (text) => {
    if (!text || typeof text !== 'string') return text || '';
    const regex = /(\*[^*]+\*|_[^_]+_|~[^~]+~|`[^`]+`)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      const token = match[0];
      const marker = token[0];
      const content = token.slice(1, -1);
      if (marker === '*') {
        parts.push(<strong key={`fmt-${key}`}>{content}</strong>);
      } else if (marker === '_') {
        parts.push(<em key={`fmt-${key}`}>{content}</em>);
      } else if (marker === '~') {
        parts.push(<span key={`fmt-${key}`} style={{ textDecoration: 'line-through' }}>{content}</span>);
      } else if (marker === '`') {
        parts.push(
          <Box
            key={`fmt-${key}`}
            component="code"
            sx={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              fontSize: '0.85em',
              px: 0.5,
              py: 0.15,
              borderRadius: 0.5,
              bgcolor: 'rgba(0,0,0,0.08)',
            }}
          >
            {content}
          </Box>
        );
      } else {
        parts.push(content);
      }
      lastIndex = regex.lastIndex;
      key += 1;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts;
  };

  const headerText = typeof template?.header === 'string' ? template.header : template?.header?.text || '';
  const bodyText = typeof template?.body === 'string' ? template.body : template?.body?.text || '';
  const footerText = typeof template?.footer === 'string' ? template.footer : template?.footer?.text || '';
  const headerTypeRaw = template?.headerType || template?.header?.type || template?.header?.format || 'none';
  const headerType = typeof headerTypeRaw === 'string' ? headerTypeRaw.toLowerCase() : 'none';

  const buttons = Array.isArray(template?.buttons) ? template.buttons : [];
  const canShowMedia = Boolean(showHeaderMedia && template?.headerMediaUrl);

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: '#e5ddd5',
        backgroundImage:
          'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAADIAAAAyCAIAAACRXR/mAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAIGNIUk0AAHolAACAgwAA+f8AAIDpAAB1MAAA6mAAADqYAAAXb5JfxUYAAABnSURBVHja7M5RDYAwDEXRDgmvEocnlrQS2SwUFST9uez9/vjuj9mZFBMfAQ0B2M0kgA8CfK0CfA1jk/4aRNgq58k4YvHlzYpVJVVEk/xNmtj/OvtKfvJfABD8H8C8AQ0A2M0kgA8CfAMAAAD//wMAkmTzYQAAAAZiS0dEAP8A/wD/oL2nkwAAAAd0SU1FB+MBBgATNjeH/ioAAABkSURBVBjTY2CgMWABY")',
        borderRadius: 2,
        p: 2,
        minHeight: 300,
      }}
    >
      <Box
        sx={{
          maxWidth: '85%',
          ml: 'auto',
        }}
      >
        <Paper
          sx={{
            bgcolor: '#dcf8c6',
            borderRadius: '8px 8px 0 8px',
            overflow: 'hidden',
          }}
        >
          {headerType && headerType !== 'none' && (
            <Box sx={{ p: 1.5, pb: 0 }}>
                {headerType === 'text' ? (
                  <Typography variant="body2" fontWeight={600}>
                    {renderFormattedText(renderWithSamples(headerText, 'header'))}
                  </Typography>
                ) : (
                <Box
                  sx={{
                    bgcolor: '#f8fafc',
                    borderRadius: 1,
                    p: 1,
                    textAlign: 'center',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  {headerType === 'image' ? (
                    canShowMedia ? (
                      <Box
                        component="img"
                        src={template.headerMediaUrl}
                        alt="Header image preview"
                        sx={{ maxWidth: '100%', borderRadius: 1, maxHeight: 200, objectFit: 'contain' }}
                      />
                    ) : (
                    <Box
                      sx={{
                        bgcolor: '#e8f4fd',
                        borderRadius: 1,
                        minHeight: 120,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        py: 3,
                      }}
                    >
                      <SvgIcon component={ImageIcon} inheritViewBox sx={{ fontSize: 40, color: '#3b82f6' }} />
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                        Image
                      </Typography>
                    </Box>
                    )
                  ) : headerType === 'video' ? (
                    canShowMedia ? (
                      <Box component="video" controls sx={{ maxWidth: '100%', borderRadius: 1, maxHeight: 220 }}>
                        <source src={template.headerMediaUrl} type="video/mp4" />
                        <source src={template.headerMediaUrl} type="video/webm" />
                        Your browser does not support the video tag.
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          bgcolor: '#fef3e8',
                          borderRadius: 1,
                          minHeight: 120,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          py: 3,
                        }}
                      >
                        <SvgIcon component={Video} inheritViewBox sx={{ fontSize: 40, color: '#f97316' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#f97316' }}>
                          Video
                        </Typography>
                      </Box>
                    )
                  ) : headerType === 'document' ? (
                    canShowMedia ? (
                      <Typography variant="caption" color="text.secondary">
                        Document attached
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          bgcolor: '#f0fdf4',
                          borderRadius: 1,
                          minHeight: 120,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 1,
                          py: 3,
                        }}
                      >
                        <SvgIcon component={FileText} inheritViewBox sx={{ fontSize: 40, color: '#22c55e' }} />
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#22c55e' }}>
                          Document
                        </Typography>
                      </Box>
                    )
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {headerType} preview
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {bodyText && (
            <Box sx={{ p: 1.5, py: template.header ? 1 : 1.5 }}>
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
              >
                {renderFormattedText(renderWithSamples(bodyText, 'body'))}
              </Typography>
            </Box>
          )}

          {footerText && (
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {renderFormattedText(renderWithSamples(footerText, 'footer'))}
              </Typography>
            </Box>
          )}

          {buttons.length > 0 && (
            <>
              <Divider />
              <Box sx={{ p: 1 }}>
                {buttons.map((button, index) => {
                  const btnType = button.type || button.action || button.tb_type;
                  const Icon = buttonIcons[btnType] || Reply;
                  const btnText = button.text || button.label || button.name || `Button ${index + 1}`;
                  return (
                    <Button
                      key={index}
                      fullWidth
                      size="small"
                      startIcon={<SvgIcon component={Icon} inheritViewBox fontSize="small" />}
                      sx={{
                        color: '#00a5f4',
                        textTransform: 'none',
                        justifyContent: 'center',
                      py: 0.75,
                      borderTop: index > 0 ? '1px solid #e0e0e0' : 'none',
                      borderRadius: 0,
                    }}
                  >
                      {btnText}
                    </Button>
                  );
                })}
              </Box>
            </>
          )}
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Box>
      </Box>

      {(!template?.body && !template?.header) && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Start typing to see preview
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default TemplatePreview;
